import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { CheckCircle, AlertCircle, Loader2, TrendingUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const PROGRESS_PROMPTS = [
  "Let's check your progress",
  "See how you've been doing this week",
  "Your trends are waiting - take a look",
  "Track your momentum so far",
  "How's your week shaping up? View progress",
];

const getLocalDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const TODAY = getLocalDate();

const MOODS = [
  { v: 1, e: "😞", label: "Rough" },
  { v: 2, e: "😕", label: "Meh" },
  { v: 3, e: "😐", label: "Okay" },
  { v: 4, e: "🙂", label: "Good" },
  { v: 5, e: "😄", label: "Great" },
];

export default function DailyCheckInCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(() => localStorage.getItem("checkin_date") === TODAY);
  const [error, setError] = useState(null);
  const [promptIndex] = useState(() => Math.floor(Math.random() * PROGRESS_PROMPTS.length));
  const [formData, setFormData] = useState({
    focus_score: 3, mood_score: 3, motivation_score: 3, sleep_quality: "okay", med_taken: false,
  });

  useEffect(() => {
    const h = () => setIsOpen(true);
    window.addEventListener("checkin:open", h);
    return () => window.removeEventListener("checkin:open", h);
  }, []);

  const handleSubmit = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      await API.post("/daily-check-ins", {
        date: getLocalDate(),
        time: now.toTimeString().split(" ")[0],
        ...formData,
      });
      localStorage.setItem("checkin_date", TODAY);
      setSubmitted(true);
      setIsOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (msg === "Not authenticated" || msg === "Token expired") {
        setError("Your session expired. Please refresh the page and log in again.");
      } else {
        setError("Failed to save check-in. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="space-y-3 animate-fade-in" data-testid="checkin-submitted">
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-green-900 text-sm">Check-in saved!</p>
              <p className="text-sm text-green-800 mt-1">Great work tracking your day. Keep it up!</p>
            </div>
          </div>
          <button onClick={() => { setSubmitted(false); localStorage.removeItem("checkin_date"); setIsOpen(true); }}
            className="text-xs text-green-700 underline hover:text-green-900 whitespace-nowrap shrink-0 mt-0.5">
            Check in again
          </button>
        </div>
        <button onClick={() => navigate("/progress")}
          className="w-full bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-xl p-4 hover:shadow-lg transition-all text-left flex items-center gap-3"
          data-testid="view-progress-button">
          <TrendingUp className="w-5 h-5 shrink-0" />
          <span className="font-semibold text-sm">{PROGRESS_PROMPTS[promptIndex]}</span>
        </button>
      </div>
    );
  }

  if (!isOpen) {
    return (
      <div data-testid="daily-checkin-button"
        className="w-full bg-gradient-to-r from-secondary to-secondary/80 text-secondary-foreground rounded-xl p-4">
        <p className="font-semibold text-sm">Daily check-in</p>
        <p className="text-xs mt-1 opacity-80">How's your mood right now? One tap.</p>
        <div className="flex gap-2 mt-3">
          {MOODS.map((m) => (
            <button
              key={m.v}
              onClick={() => { setFormData((f) => ({ ...f, mood_score: m.v })); setIsOpen(true); }}
              aria-label={m.label}
              data-testid={`mood-quick-${m.v}`}
              className="flex-1 bg-white/20 hover:bg-white/40 active:scale-95 rounded-lg py-2 text-xl transition-all"
            >
              {m.e}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-4 animate-fade-in" data-testid="daily-checkin-form">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">How are you feeling today?</h3>
        <button onClick={() => setIsOpen(false)} className="p-1 rounded-lg hover:bg-muted transition-colors"><X className="w-4 h-4 text-muted-foreground" /></button>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" /><p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      <div>
        <label className="text-xs font-semibold uppercase text-foreground tracking-widest">Focus today: {formData.focus_score}/5</label>
        <input type="range" min="1" max="5" value={formData.focus_score} onChange={(e) => setFormData({ ...formData, focus_score: parseInt(e.target.value) })} className="w-full mt-1.5 accent-primary" data-testid="focus-slider" />
        <div className="flex justify-between text-xs text-muted-foreground mt-1"><span>Struggling</span><span>On fire</span></div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase text-foreground tracking-widest">Mood today: {formData.mood_score}/5</label>
        <input type="range" min="1" max="5" value={formData.mood_score} onChange={(e) => setFormData({ ...formData, mood_score: parseInt(e.target.value) })} className="w-full mt-1.5 accent-primary" data-testid="mood-slider" />
        <div className="flex justify-between text-xs text-muted-foreground mt-1"><span>Down</span><span>Great</span></div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase text-foreground tracking-widest">Motivation today: {formData.motivation_score}/5</label>
        <input type="range" min="1" max="5" value={formData.motivation_score} onChange={(e) => setFormData({ ...formData, motivation_score: parseInt(e.target.value) })} className="w-full mt-1.5 accent-primary" data-testid="motivation-slider" />
        <div className="flex justify-between text-xs text-muted-foreground mt-1"><span>Unmotivated</span><span>Unstoppable</span></div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase text-foreground tracking-widest block mb-2">Sleep last night</label>
        <div className="flex gap-2">
          {["poor", "okay", "good"].map((q) => (
            <button key={q} onClick={() => setFormData({ ...formData, sleep_quality: q })} data-testid={`sleep-${q}`}
              className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${formData.sleep_quality === q ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
              {q.charAt(0).toUpperCase() + q.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase text-foreground tracking-widest block mb-2">Medication taken?</label>
        <div className="flex gap-2">
          <button onClick={() => setFormData({ ...formData, med_taken: true })} data-testid="med-yes"
            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${formData.med_taken ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>Yes</button>
          <button onClick={() => setFormData({ ...formData, med_taken: false })} data-testid="med-no"
            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${!formData.med_taken ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>Not Yet</button>
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <Button onClick={handleSubmit} disabled={loading} className="flex-1 gap-2 rounded-full" data-testid="daily-checkin-submit-button">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit"}
        </Button>
        <Button onClick={() => setIsOpen(false)} variant="outline" className="flex-1 rounded-full">Skip</Button>
      </div>
    </div>
  );
}
